import { ArrowUpRight } from "lucide-react";
import { BRAND, UI } from "@/content";
import { useLang } from "@/LangContext";
import { scrollToId } from "@/lib/royale";
import LotusMark from "./LotusMark";

export default function Footer() {
  const { lang } = useLang();
  const t = UI[lang] || UI.es;
  const year = new Date().getFullYear();

  const links = [
    { id: "tratamientos", label: t.nav.treatments },
    { id: "galeria", label: t.nav.gallery },
    { id: "bono", label: lang === "es" ? "Bonos" : "Vouchers" },
    { id: "contacto", label: t.nav.contact },
  ];

  return (
    <footer className="relative border-t border-gold/15 bg-pine overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-14 pb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <LotusMark className="w-8 h-8 text-gold" />
              <span className="font-serif text-3xl tracking-[0.3em] text-cream">ROYALE</span>
            </div>
            <p className="mt-4 text-sage font-light text-sm leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-goldbright mb-5">
              {t.nav.gallery === "Gallery" ? "Explore" : "Explora"}
            </p>
            <ul className="flex flex-col gap-3">
              {links.map((l) => (
                <li key={l.id}>
                  <a
                    href={`#${l.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(l.id);
                    }}
                    data-testid={`footer-link-${l.id}`}
                    className="text-sm text-sage hover:text-cream transition-colors duration-300"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-goldbright mb-5">
              {t.contact.eyebrow}
            </p>
            <ul className="flex flex-col gap-3 text-sm text-sage">
              <li>
                <a
                  href={BRAND.phoneHref}
                  data-testid="footer-phone-link"
                  className="hover:text-goldbright transition-colors duration-300"
                >
                  {BRAND.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="hover:text-wamist transition-colors duration-300"
                >
                  WhatsApp
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="footer-email-link"
                  className="hover:text-cream transition-colors duration-300 break-all"
                >
                  {BRAND.email}
                </a>
              </li>
              <li className="leading-relaxed">{BRAND.address}</li>
            </ul>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-goldbright mb-5">
              {t.contact.hoursTitle}
            </p>
            <ul className="flex flex-col gap-3 text-sm text-sage">
              <li>{t.contact.days}</li>
              <li className="font-mono text-cream">{t.contact.hoursVal}</li>
              <li>
                {t.contact.sunday} · <span className="text-gold">{t.contact.closed}</span>
              </li>
              <li className="pt-2">
                <a
                  href={BRAND.fresha}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-fresha-link"
                  className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-gold hover:text-goldbright transition-colors duration-300"
                >
                  {t.footer.bookNote}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div
          aria-hidden="true"
          className="font-serif font-medium text-[18vw] leading-[0.8] text-center text-outline-gold select-none pointer-events-none mt-10 -mb-2"
          data-testid="footer-wordmark"
        >
          ROYALE
        </div>

        <div className="relative border-t border-gold/15 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-sage/70">
            © {year} {BRAND.full}. {t.footer.rights}
          </p>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-sage/70">
            Sants-Montjuïc · Barcelona
          </p>
        </div>
      </div>
    </footer>
  );
}
